import { Fragment } from 'react';
import { Outlet } from 'react-router-dom';
import { useSelector } from 'react-redux';
import cx from 'classnames';
import { useResizeDetector } from 'react-resize-detector';
import { ToastContainer } from 'react-toastify';

import AppHeader from './Layout/AppHeader';
import AppSidebar from './Layout/AppSidebar';
import AppFooter from './Layout/AppFooter';
import ThemeOptions from './Layout/ThemeOptions';
import useDarkModeSync from './hooks/useDarkModeSync';

/**
 * Orbit MainShell — adaptiert das DemoPages/Main Layout-Pattern, aber:
 *  - kein `AppMain`-Wrapper mit eigenen Routes; statt dessen <Outlet/>
 *    direkt im app-main__inner. Routes werden von OrbitRoutes definiert.
 *  - ThemeOptions-Panel nur im Dev-Build sichtbar.
 */
export function MainShell() {
  useDarkModeSync();
  const colorScheme = useSelector((s) => s.ThemeOptions.colorScheme);
  const enableFixedHeader = useSelector((s) => s.ThemeOptions.enableFixedHeader);
  const enableFixedSidebar = useSelector((s) => s.ThemeOptions.enableFixedSidebar);
  const enableFixedFooter = useSelector((s) => s.ThemeOptions.enableFixedFooter);
  const enableClosedSidebar = useSelector((s) => s.ThemeOptions.enableClosedSidebar);
  const enableMobileMenu = useSelector((s) => s.ThemeOptions.enableMobileMenu);
  const enablePageTabsAlt = useSelector((s) => s.ThemeOptions.enablePageTabsAlt);

  const { width, ref } = useResizeDetector();

  return (
    <Fragment>
      <a className="skip-link" href="#main-content">
        Skip to main content
      </a>
      {import.meta.env.DEV && <ThemeOptions />}
      <div ref={ref}>
        <div
          className={cx(
            'app-container app-theme-' + colorScheme,
            { 'fixed-header': enableFixedHeader },
            { 'fixed-sidebar': enableFixedSidebar || width < 992 },
            { 'fixed-footer': enableFixedFooter },
            { 'closed-sidebar': enableClosedSidebar || width < 992 },
            { 'closed-sidebar-mobile': width < 992 },
            { 'sidebar-mobile-open': enableMobileMenu },
            { 'body-tabs-shadow-btn': enablePageTabsAlt },
          )}
        >
          <AppHeader />
          <div className="app-main">
            <AppSidebar />
            <div className="app-main__outer">
              <main id="main-content" className="app-main__inner" tabIndex={-1}>
                <Outlet />
              </main>
              <AppFooter />
            </div>
          </div>
        </div>
      </div>
      <ToastContainer />
    </Fragment>
  );
}
